import {
  getPreferredIdForStop,
  Leg,
  legContainsStop,
  Stop,
  stopsAreSameStop,
} from "@vygruppen/vy-train-map";
import { log, LogLevel } from "logging/datadogBrowserLogs";

export const stopNameWithFallback = (stop: Stop) =>
  (stop.name || getPreferredIdForStop(stop)?.name) ??
  stop.stopIds.at(0)?.name ??
  stop.stopIds.at(0)?.id ??
  "STOP ID MISSING";

const findEndpoints = (legs: Leg[]) => {
  const allPoints = legs.flatMap((leg) => [leg.fromStop, leg.toStop]);
  const endpoints = allPoints.filter((point, index, self) => {
    const pointsBeforeIndex = self.slice(0, index);
    const pointsAfterIndex = self.slice(index + 1);
    const pointIsRepeated =
      pointsBeforeIndex.some((pointBefore) =>
        stopsAreSameStop(point, pointBefore),
      ) ||
      pointsAfterIndex.some((pointAfter) =>
        stopsAreSameStop(point, pointAfter),
      );
    return !pointIsRepeated;
  });
  return endpoints;
};

const findNextLegIndex = (stop: Stop, legsToSearch: Leg[]) => {
  const nextLegIndex = legsToSearch.findIndex((leg) =>
    legContainsStop(leg, stop),
  );
  // Hvis stoppet ikke finnes i noen legs, er ikke valgt strekning sammenhengende, og vi hopper til neste strekningsdel
  return nextLegIndex !== -1 ? nextLegIndex : 0;
};

const isStopEndpoint = (stop: Stop, endpoints: Stop[]) =>
  endpoints.some((endpoint) => stopsAreSameStop(stop, endpoint));

const findVisitedStopWithMoreConnections = (
  visitedStops: Stop[],
  remainingLegs: Leg[],
) =>
  visitedStops.find((stop) =>
    remainingLegs.some((leg) => legContainsStop(leg, stop)),
  );

export const generateStretchName = (selectedLegs: Leg[]) => {
  try {
    const remainingLegs = [...selectedLegs];
    const stretchNames: string[] = [];

    while (remainingLegs.length > 0) {
      const endpoints = findEndpoints(remainingLegs);
      const start = endpoints[0];
      let currentStop = start;
      let hasFoundEnd = false;

      const visitedStops: Stop[] = [];

      if (endpoints.length === 0) {
        break;
      }

      if (endpoints.length === 1) {
        stretchNames.push(stopNameWithFallback(endpoints[0]));
        break;
      }

      while (!hasFoundEnd && endpoints.length > 0 && remainingLegs.length > 0) {
        visitedStops.push(currentStop);

        const nextLegIndex = findNextLegIndex(currentStop, remainingLegs);

        const nextStop =
          remainingLegs[nextLegIndex] &&
          stopsAreSameStop(remainingLegs[nextLegIndex].fromStop, currentStop)
            ? remainingLegs[nextLegIndex].toStop
            : remainingLegs[nextLegIndex].fromStop;

        remainingLegs.splice(nextLegIndex, 1);

        const nextStopIsEndpoint = isStopEndpoint(nextStop, endpoints);
        if (nextStopIsEndpoint) {
          hasFoundEnd = true;
          stretchNames.push(
            `${stopNameWithFallback(start)}\u2013${stopNameWithFallback(nextStop)}`,
          );
        } else if (
          visitedStops.some((stop) => stopsAreSameStop(stop, nextStop))
        ) {
          const nextNextStop = findVisitedStopWithMoreConnections(
            visitedStops,
            remainingLegs,
          );
          if (nextNextStop) {
            currentStop = nextNextStop;
          }
        } else {
          currentStop = nextStop;
        }
      }
    }
    return stretchNames;
  } catch {
    log(
      LogLevel.error,
      "generateStretchName",
      `Failed generating stretchName for selectedLegs ${selectedLegs.map((leg) => `${leg.fromStop.name}-${leg.toStop.name}`).join(", ")}`,
    );
    return [];
  }
};

export const generateSingleLegStretchName = (
  leg: Leg,
  clickedStops: Stop[],
) => {
  const firstStop =
    clickedStops.find((stop) => legContainsStop(leg, stop)) ?? leg.fromStop;

  const secondStop = stopsAreSameStop(firstStop, leg.fromStop)
    ? leg.toStop
    : leg.fromStop;

  return [
    `${stopNameWithFallback(firstStop)}\u2013${stopNameWithFallback(secondStop)}`,
  ];
};
